import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { Link } from "react-router-dom"
import AppLayout from "@/components/app/AppLayout"
import { FieldPalette } from "@/components/care-plan-builder/FieldPalette"
import { FieldInspector } from "@/components/care-plan-builder/FieldInspector"
import { TemplateCanvas } from "@/components/care-plan-builder/TemplateCanvas"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { API_BASE } from "@/config/api"
import { CARE_PLAN_MODALITIES } from "@/config/clinical-verticals"
import { emptyDefinition, newFieldId } from "@/lib/care-plan-form-utils"
import { toast } from "sonner"
import { Save, Copy } from "lucide-react"

const API = API_BASE

export default function CarePlanTemplates() {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState([])
  const [library, setLibrary] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [name, setName] = useState("")
  const [modality, setModality] = useState("psych")
  const [definition, setDefinition] = useState(emptyDefinition("psych"))
  const [selectedFieldId, setSelectedFieldId] = useState(null)
  const [activeSectionId, setActiveSectionId] = useState(null)

  const load = useCallback(async () => {
    try {
      const [tRes, lRes] = await Promise.all([
        axios.get(`${API}/care-plan-templates`),
        axios.get(`${API}/care-plan-field-library`),
      ])
      setTemplates(tRes.data)
      setLibrary(lRes.data)
    } catch {
      toast.error("Nu s-au putut încărca șabloanele")
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const selectTemplate = (tpl) => {
    setSelectedId(tpl.id)
    setName(tpl.name)
    setModality(tpl.modality)
    setDefinition(tpl.definition || emptyDefinition(tpl.modality))
    setSelectedFieldId(null)
  }

  const selectedField = (() => {
    for (const sec of definition.sections || []) {
      const f = (sec.fields || []).find((x) => x.id === selectedFieldId)
      if (f) return f
    }
    return null
  })()

  const updateField = (updated) => {
    setDefinition((def) => ({
      ...def,
      sections: (def.sections || []).map((sec) => ({
        ...sec,
        fields: (sec.fields || []).map((f) => (f.id === updated.id ? updated : f)),
      })),
    }))
  }

  const addFieldToSection = (sectionId, field) => {
    setDefinition((def) => ({
      ...def,
      sections: (def.sections || []).map((sec) =>
        sec.id === sectionId ? { ...sec, fields: [...(sec.fields || []), field] } : sec
      ),
    }))
    setSelectedFieldId(field.id)
    setActiveSectionId(sectionId)
  }

  const save = async () => {
    if (!name.trim()) return toast.error("Nume obligatoriu")
    try {
      if (selectedId && templates.find((t) => t.id === selectedId && !t.is_system)) {
        await axios.patch(`${API}/care-plan-templates/${selectedId}`, { name, definition })
        toast.success("Salvat")
      } else if (selectedId && templates.find((t) => t.id === selectedId)?.is_system) {
        const { data } = await axios.post(`${API}/care-plan-templates/${selectedId}/fork`, { name: `${name} — cabinet` })
        await axios.patch(`${API}/care-plan-templates/${data.id}`, { name, definition })
        setSelectedId(data.id)
        toast.success("Șablon fork-uit și salvat")
      } else {
        const { data } = await axios.post(`${API}/care-plan-templates`, { name, modality, definition })
        setSelectedId(data.id)
        toast.success("Creat")
      }
      load()
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare salvare")
    }
  }

  const forkSelected = async () => {
    if (!selectedId) return
    try {
      const { data } = await axios.post(`${API}/care-plan-templates/${selectedId}/fork`, {})
      selectTemplate(data)
      load()
      toast.success("Copie creată — o poți personaliza")
    } catch {
      toast.error("Fork eșuat")
    }
  }

  const libForModality = library.filter((i) => i.modality === modality)

  return (
    <AppLayout
      title={t("app.carePlanTemplates.pageTitle")}
      actions={
        <Button size="sm" className="rounded-full" onClick={save}>
          <Save className="h-4 w-4 mr-1" /> {t("app.carePlanTemplates.save")}
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground mb-4">
        Constructor vizual pentru planuri conforme legislației RO.{" "}
        <Link to="/app/care-plans" className="text-accent underline">Planuri pacienți</Link>
      </p>
      <div className="grid lg:grid-cols-12 gap-4">
        <aside className="lg:col-span-2 space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Șabloane</p>
          <ul className="rounded-xl border divide-y max-h-[60vh] overflow-y-auto bg-card text-sm">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`w-full text-left px-3 py-2 hover:bg-muted/50 ${selectedId === t.id ? "bg-secondary font-medium" : ""}`}
                  onClick={() => selectTemplate(t)}
                >
                  <span className="block truncate">{t.name}</span>
                  <span className="text-[10px] text-muted-foreground">{t.modality}{t.is_system ? " · sistem" : ""}</span>
                </button>
              </li>
            ))}
          </ul>
          <Button type="button" variant="outline" className="w-full rounded-full text-xs" onClick={() => {
            setSelectedId(null)
            setName("Șablon nou")
            setDefinition(emptyDefinition(modality))
          }}>
            + Șablon gol
          </Button>
        </aside>

        <div className="lg:col-span-7 space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <Label>Nume</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="w-40">
              <Label>Modalitate</Label>
              <Select value={modality} onValueChange={(v) => { setModality(v); setDefinition((d) => ({ ...d, modality: v })) }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARE_PLAN_MODALITIES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedId && (
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={forkSelected}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Fork
              </Button>
            )}
          </div>
          <TemplateCanvas
            definition={definition}
            onChange={setDefinition}
            selectedFieldId={selectedFieldId}
            onSelectField={setSelectedFieldId}
            onAddFieldToSection={(sid) => {
              setActiveSectionId(sid)
              addFieldToSection(sid, { id: newFieldId(), type: "text", label: "Câmp nou", required: false })
            }}
          />
        </div>

        <aside className="lg:col-span-3 space-y-4">
          <FieldPalette
            libraryItems={libForModality}
            onAddField={(field) => {
              const sid = activeSectionId || definition.sections?.[0]?.id
              if (sid) addFieldToSection(sid, field)
              else toast.error("Adaugă o secțiune mai întâi")
            }}
            onAddFromLibrary={(item) => {
              const sid = activeSectionId || definition.sections?.[0]?.id
              if (!sid) return toast.error("Adaugă o secțiune")
              const fd = item.field_def || {}
              addFieldToSection(sid, { ...fd, id: newFieldId(), label: item.label })
            }}
          />
          <FieldInspector field={selectedField} onChange={updateField} />
        </aside>
      </div>
    </AppLayout>
  )
}
